'use client';
import { gql } from '@apollo/client';
import { ReportSectionState } from '@mnd-frontend/graphql-schema';
import { useMediaQuery } from '@mnd-frontend/hooks';
import { toBoolean } from '@mnd-frontend/ts';
import { Icons, Text } from '@mnd-frontend/ui/publish/components';
import { media } from '@mnd-frontend/ui/publish/utils';
import { GREY_500, GREY_800, GREY_900 } from '@mnd-frontend/ui/shared/tokens';
import { localizeValue } from '@mnd-frontend/utils';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import type { Report2VisibilityOverviewFragment } from '../../../../../generated/types';
import { useReportStaticData, useReportTranslation } from '../../../hooks';
import type { TranslationFunction } from '../../../types';
import { Section, SectionHeader, SkeletonTable, TrendPill } from '../../common';
import { Card } from '../../common/Card';

export const REPORT2_VISIBILITY_OVERVIEW_FRAGMENT = gql`
  fragment Report2VisibilityOverview on ReportVisibilityOverview {
    id
    title
    state
    showArticleData
    showAudioVideoData
    showSocialMediaData
    summary {
      articleReachCurrentYear
      audioVideoReachCurrentYear
      socialMediaReachCurrentYear
      showAudioVideoDataCurrentYearUncompleteNotice
      showSocialMediaDataCurrentYearUncompleteNotice
      totalReachCurrentYear
      articleReachPreviousYear
      audioVideoReachPreviousYear
      socialMediaReachPreviousYear
      showAudioVideoDataPreviousYearUncompleteNotice
      showSocialMediaDataPreviousYearUncompleteNotice
      totalReachPreviousYear
      articleReachTrend
      audioVideoReachTrend
      socialMediaReachTrend
      totalReachTrend
      articleMentionsCurrentYear
      audioVideoMentionsCurrentYear
      socialMediaMentionsCurrentYear
      totalMentionsCurrentYear
      articleMentionsPreviousYear
      audioVideoMentionsPreviousYear
      socialMediaMentionsPreviousYear
      totalMentionsPreviousYear
      articleMentionsTrend
      audioVideoMentionsTrend
      socialMediaMentionsTrend
      totalMentionsTrend
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: ${GREY_800};
`;

const Uppercase = styled.span`
  text-transform: uppercase;
`;

const Th = styled.th`
  text-align: right;

  &:first-child {
    text-align: left;
    padding: 0 0.25rem 0 0;
  }
  &:last-child {
    padding: 0 0 0 0.25rem;
  }
`;

const Tables = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex-direction: column;
  > * {
    width: 100%;
  }
`;

const Td = styled.td`
  padding: 0 0.25rem;
  text-align: right;
  width: calc(100% / 3);

  ${media.greaterThan('phablet')} {
    width: calc(50% / 3);
    &:first-child {
      width: 50%;
    }
  }

  &:first-child {
    text-align: left;
    padding: 0 0.25rem 0 0;
  }
  &:last-child {
    padding: 0 0 0 0.25rem;
  }
`;

const Tr = styled.tr`
  ${media.greaterThan('phablet')} {
    &:nth-last-child(2) {
      ${Td} {
        padding-bottom: 1rem;
      }
    }
    &:last-child {
      ${Td} {
        padding-top: 1rem;
        border-top: 1px solid ${GREY_500};
      }
    }
  }
`;
const IconWrapper = styled.span`
  color: var(--coverage-brand-color);
  position: relative;
  top: -0.125rem;
  display: inline-block;
  margin-right: 0.5rem;
  ${media.lessThan('tablet')} {
    display: none;
    margin: 0;
  }
`;
const MobileSectionHeader = styled.div``;

type SummaryOverviewData = Report2VisibilityOverviewFragment['summary'];

enum CellStyle {
  SUMMARY = 'summary',
  TREND = 'trend',
}
interface CellData {
  value: number | string;
  style?: CellStyle;
  icon?: ReactNode;
}

const generateTableData = ({
  t,
  data,
  type,
  year,
  prevYear,
  hasSoMe,
  hasAudioVideo,
  hasTraditional,
}: {
  t: TranslationFunction;
  data: SummaryOverviewData;
  type: 'Mentions' | 'Reach';
  year: number;
  prevYear: boolean;
  hasSoMe: boolean;
  hasAudioVideo: boolean;
  hasTraditional: boolean;
}) => {
  const title =
    type === 'Mentions' ? t('reports_key_metrics_mentions') : t('reports_key_metrics_reach');
  const headers = [title, ...(prevYear ? [year - 1, year, t('reports_trend')] : [year])].map(val =>
    val.toString(),
  );

  let asterisk = '*';

  const rows: CellData[][] = [];
  if (hasAudioVideo) {
    const audioVideoRow = [
      { value: t('reports_audio_video') },
      ...(prevYear
        ? [
            {
              value:
                data[`audioVideo${type}PreviousYear`] +
                (data.showAudioVideoDataPreviousYearUncompleteNotice ? asterisk : ''),
            },
            {
              value:
                data[`audioVideo${type}CurrentYear`] +
                (data.showAudioVideoDataCurrentYearUncompleteNotice ? asterisk : ''),
            },
            { value: data[`audioVideo${type}Trend`], style: CellStyle.TREND },
          ]
        : [
            {
              value:
                data[`audioVideo${type}CurrentYear`] +
                (data.showAudioVideoDataCurrentYearUncompleteNotice ? asterisk : ''),
            },
          ]),
    ];
    asterisk += '*';
    rows.push(audioVideoRow);
  }
  if (hasSoMe) {
    const soMeRow = [
      { value: t('reports_social_media') },
      ...(prevYear
        ? [
            {
              value:
                data[`socialMedia${type}PreviousYear`] +
                (data.showSocialMediaDataPreviousYearUncompleteNotice ? asterisk : ''),
            },
            {
              value:
                data[`socialMedia${type}CurrentYear`] +
                (data.showSocialMediaDataCurrentYearUncompleteNotice ? asterisk : ''),
            },
            { value: data[`socialMedia${type}Trend`], style: CellStyle.TREND },
          ]
        : [
            {
              value:
                data[`socialMedia${type}CurrentYear`] +
                (data.showSocialMediaDataCurrentYearUncompleteNotice ? asterisk : ''),
            },
          ]),
    ];
    rows.push(soMeRow);
  }
  if (hasTraditional) {
    const traditionalRow = [
      { value: t('reports_traditional_media') },
      ...(prevYear
        ? [
            { value: data[`article${type}PreviousYear`] },
            { value: data[`article${type}CurrentYear`] },
            { value: data[`article${type}Trend`], style: CellStyle.TREND },
          ]
        : [{ value: data[`article${type}CurrentYear`] }]),
    ];
    rows.push(traditionalRow);
  }

  rows.push([
    {
      value:
        type === 'Mentions'
          ? t('reports_total_media_mentions')
          : t('reports_total_reach_impressions'),
      style: CellStyle.SUMMARY,
      icon: type === 'Mentions' ? <Icons.Story /> : <Icons.Reach />,
    },
    ...(prevYear
      ? [
          { value: data[`total${type}PreviousYear`], style: CellStyle.SUMMARY },
          { value: data[`total${type}CurrentYear`], style: CellStyle.SUMMARY },
          { value: data[`total${type}Trend`], style: CellStyle.TREND },
        ]
      : [{ value: data[`total${type}CurrentYear`], style: CellStyle.SUMMARY }]),
  ]);

  return { header: headers, rows };
};
const generateTableDataMobile = ({
  t,
  data,
  type,
  section,
  year,
  prevYear,
}: {
  t: TranslationFunction;
  data: SummaryOverviewData;
  type: 'Mentions' | 'Reach';
  section: 'SoMe' | 'Traditional' | 'Audio & Video' | 'Total';
  year: number;
  prevYear: boolean;
}) => {
  const headers = [...(prevYear ? [year - 1, year, t('reports_trend')] : [year])].map(val =>
    val.toString(),
  );
  const asterisk = '*';
  const asteriskSocialMedia =
    data.showAudioVideoDataPreviousYearUncompleteNotice ||
    data.showAudioVideoDataCurrentYearUncompleteNotice
      ? '**'
      : '*';

  const rows: CellData[][] = [
    section === 'Audio & Video'
      ? [
          ...(prevYear
            ? [
                {
                  value:
                    data[`audioVideo${type}PreviousYear`] +
                    (data.showAudioVideoDataPreviousYearUncompleteNotice ? asterisk : ''),
                },
                {
                  value:
                    data[`audioVideo${type}CurrentYear`] +
                    (data.showAudioVideoDataCurrentYearUncompleteNotice ? asterisk : ''),
                },
                { value: data[`audioVideo${type}Trend`], style: CellStyle.TREND },
              ]
            : [
                {
                  value:
                    data[`audioVideo${type}CurrentYear`] +
                    (data.showAudioVideoDataCurrentYearUncompleteNotice ? asterisk : ''),
                },
              ]),
        ]
      : null,
    section === 'SoMe'
      ? [
          ...(prevYear
            ? [
                {
                  value:
                    data[`socialMedia${type}PreviousYear`] +
                    (data.showSocialMediaDataPreviousYearUncompleteNotice
                      ? asteriskSocialMedia
                      : ''),
                },
                {
                  value:
                    data[`socialMedia${type}CurrentYear`] +
                    (data.showSocialMediaDataCurrentYearUncompleteNotice
                      ? asteriskSocialMedia
                      : ''),
                },
                { value: data[`socialMedia${type}Trend`], style: CellStyle.TREND },
              ]
            : [
                {
                  value:
                    data[`socialMedia${type}CurrentYear`] +
                    (data.showSocialMediaDataCurrentYearUncompleteNotice
                      ? asteriskSocialMedia
                      : ''),
                },
              ]),
        ]
      : null,
    section === 'Traditional'
      ? [
          ...(prevYear
            ? [
                { value: data[`article${type}PreviousYear`] },
                { value: data[`article${type}CurrentYear`] },
                { value: data[`article${type}Trend`], style: CellStyle.TREND },
              ]
            : [{ value: data[`article${type}CurrentYear`] }]),
        ]
      : null,
    section === 'Total'
      ? [
          ...(prevYear
            ? [
                { value: data[`total${type}PreviousYear`], style: CellStyle.SUMMARY },
                { value: data[`total${type}CurrentYear`], style: CellStyle.SUMMARY },
                { value: data[`total${type}Trend`], style: CellStyle.TREND },
              ]
            : [{ value: data[`total${type}CurrentYear`], style: CellStyle.SUMMARY }]),
        ]
      : null,
  ].filter(toBoolean);
  return { header: headers, rows };
};

const TableComponent = ({ header, rows }: { header: string[]; rows: CellData[][] }) => (
  <Table>
    <thead>
      <tr>
        {header.map((header, index) => (
          <Th key={index}>
            <Text as="span" variant="reportLabelMedium">
              <Uppercase>{header}</Uppercase>
            </Text>
          </Th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, rowIndex) => {
        return (
          <Tr key={rowIndex}>
            {row.map((cell, cellIdex) => {
              return (
                <Td key={cellIdex}>
                  {cell.icon && <IconWrapper aria-hidden="true">{cell.icon}</IconWrapper>}

                  {cell.style === CellStyle.TREND ? (
                    (Number.parseFloat(cell.value as string) || 0) !== 0 ? (
                      <TrendPill
                        direction={(cell.value as number) > 0 ? 'positive' : 'negative'}
                        value={Math.round(cell.value as number) + '%'}
                      />
                    ) : (
                      '-'
                    )
                  ) : (
                    <Text
                      variant={
                        cell.style === CellStyle.SUMMARY ? 'headlineSmall' : 'reportLabelSmall'
                      }
                      mobileVariant={
                        cell.style === CellStyle.SUMMARY ? 'reportLabelMedium' : 'reportLabelSmall'
                      }
                      as="span"
                    >
                      {localizeValue(cell.value)}
                    </Text>
                  )}
                </Td>
              );
            })}
          </Tr>
        );
      })}
    </tbody>
  </Table>
);
const Divider = styled.div`
  border-top: 1px solid ${GREY_500};
  margin: 0.5rem 0 0;
`;
const VisibilityOverview = ({ section }: { section: Report2VisibilityOverviewFragment }) => {
  const { fromDate, compareToPreviousYear } = useReportStaticData();
  const year = new Date(fromDate).getFullYear();
  const { t } = useReportTranslation();
  const hasSoMe = section?.showSocialMediaData;
  const hasAudioVideo = section?.showAudioVideoData;
  const hasTraditional = section?.showArticleData;

  const lessThanPhablet = useMediaQuery(media.lessThan('phablet'));
  const asteriskAudio = '*';
  const asteriskSocialMedia =
    section.summary.showAudioVideoDataCurrentYearUncompleteNotice ||
    section.summary.showAudioVideoDataPreviousYearUncompleteNotice
      ? '**'
      : '*';

  return (
    <Section>
      <SectionHeader
        color={GREY_900}
        as="h3"
        variant="reportHeadlineLarge"
        section={section}
        isSubsection
      />
      {section.state !== ReportSectionState.Done ? (
        <SkeletonTable />
      ) : (
        <>
          {!lessThanPhablet && (
            <>
              <Card>
                <TableComponent
                  {...generateTableData({
                    t,
                    data: section.summary,
                    type: 'Mentions',
                    year,
                    prevYear: compareToPreviousYear,
                    hasSoMe,
                    hasAudioVideo,
                    hasTraditional,
                  })}
                />
              </Card>
              <Card>
                <TableComponent
                  {...generateTableData({
                    t,
                    data: section.summary,
                    type: 'Reach',
                    year,
                    prevYear: compareToPreviousYear,
                    hasSoMe,
                    hasAudioVideo,
                    hasTraditional,
                  })}
                />
              </Card>
            </>
          )}

          {lessThanPhablet && (
            <>
              <MobileSectionHeader>
                <Uppercase>
                  <Text variant="reportHeadlineMedium" as="h4">
                    {t('reports_key_metrics_mentions')}
                  </Text>
                </Uppercase>
              </MobileSectionHeader>
              <Card>
                <Tables>
                  {hasAudioVideo && (
                    <div>
                      <Text variant="reportLabelSmall" as="h5">
                        {t('reports_audio_video')}
                      </Text>
                      <TableComponent
                        {...generateTableDataMobile({
                          t,
                          data: section.summary,
                          type: 'Mentions',
                          section: 'Audio & Video',
                          year,
                          prevYear: compareToPreviousYear,
                        })}
                      />
                    </div>
                  )}
                  {hasSoMe && (
                    <div>
                      <Text variant="reportLabelSmall" as="h5">
                        {t('reports_social_media')}
                      </Text>
                      <TableComponent
                        {...generateTableDataMobile({
                          t,
                          data: section.summary,
                          type: 'Mentions',
                          section: 'SoMe',
                          year,
                          prevYear: compareToPreviousYear,
                        })}
                      />
                    </div>
                  )}
                  {hasTraditional && (
                    <div>
                      <Text variant="reportLabelSmall" as="h5">
                        {t('reports_traditional_media')}
                      </Text>
                      <TableComponent
                        {...generateTableDataMobile({
                          t,
                          data: section.summary,
                          type: 'Mentions',
                          section: 'Traditional',
                          year,
                          prevYear: compareToPreviousYear,
                        })}
                      />
                    </div>
                  )}
                  <Divider />
                  <div>
                    <Text variant="reportLabelMedium" as="h5">
                      {t('reports_total_media_mentions')}
                    </Text>
                    <TableComponent
                      {...generateTableDataMobile({
                        t,
                        data: section.summary,
                        type: 'Mentions',
                        section: 'Total',
                        year,
                        prevYear: compareToPreviousYear,
                      })}
                    />
                  </div>
                </Tables>
              </Card>
              <MobileSectionHeader>
                <Uppercase>
                  <Text variant="reportHeadlineMedium" as="h4">
                    {t('reports_key_metrics_reach')}
                  </Text>
                </Uppercase>
              </MobileSectionHeader>
              <Card>
                <Tables>
                  {hasSoMe && (
                    <>
                      <div>
                        <Text variant="reportLabelSmall" as="h5">
                          {t('reports_social_media')}
                        </Text>
                        <TableComponent
                          {...generateTableDataMobile({
                            t,
                            data: section.summary,
                            type: 'Reach',
                            section: 'SoMe',
                            year,
                            prevYear: compareToPreviousYear,
                          })}
                        />
                      </div>

                      <div>
                        <Text variant="reportLabelSmall" as="h5">
                          {t('reports_traditional_media')}
                        </Text>
                        <TableComponent
                          {...generateTableDataMobile({
                            t,
                            data: section.summary,
                            type: 'Reach',
                            section: 'Traditional',
                            year,
                            prevYear: compareToPreviousYear,
                          })}
                        />
                      </div>
                      <Divider />
                    </>
                  )}
                  <div>
                    <Text variant="reportLabelMedium" as="h5">
                      {t('reports_total_reach_impressions')}
                    </Text>
                    <TableComponent
                      {...generateTableDataMobile({
                        t,
                        data: section.summary,
                        type: 'Reach',
                        section: 'Total',
                        year,
                        prevYear: compareToPreviousYear,
                      })}
                    />
                  </div>
                </Tables>
              </Card>
            </>
          )}
        </>
      )}
      {(section.summary.showAudioVideoDataCurrentYearUncompleteNotice ||
        section.summary.showAudioVideoDataPreviousYearUncompleteNotice) && (
        <Text as="p" variant="reportLabelSmall" color="GREY_800">
          {asteriskAudio + t('reports_audio_video_data_uncomplete_notice')}
        </Text>
      )}
      {(section.summary.showSocialMediaDataCurrentYearUncompleteNotice ||
        section.summary.showSocialMediaDataPreviousYearUncompleteNotice) && (
        <Text as="p" variant="reportLabelSmall" color="GREY_800">
          {asteriskSocialMedia + t('reports_social_media_data_uncomplete_notice')}
        </Text>
      )}
    </Section>
  );
};

export default VisibilityOverview;
